import { FC, useEffect, useState } from 'react';
import subscriptionListClass from '../../assets/scss/SubscriptionList/SubscriptionList.module.scss';
import { useMasterContext } from '../../context';
import { PayBackPointsRateModalViewType } from '../PayBackPointsRateModal/PayBackPointsRateModal';
import { calcPointsRateProgress } from '../../utils/calcPointsRateProgress';
import { Subscription } from '../../types/constant/SubscriptionType';
import { BalloonHtml } from '../../api/graphql/master/types';

export const PayBackPointsRate: FC<{
  subscription: Subscription;
  setPointsRateModal: React.Dispatch<React.SetStateAction<PayBackPointsRateModalViewType | null>>;
}> = ({ subscription, setPointsRateModal }) => {
  const { settings } = useMasterContext();

  const [shippingFreeMessage, setShippingFreeMessage] = useState('');
  const [percent, setPercent] = useState(1);
  const [missingAmount, setMissingAmount] = useState('0');
  const [progressWidth, setProgressWidth] = useState('0');
  const [balloonHtml, setBalloonHtml] = useState<BalloonHtml | null>(null);

  useEffect(() => {
    if (!subscription || !settings) return;

    // 送料無料クーポン適用
    const shippingCoupon = subscription.subscriptionDiscounts.some(
      (discount) => discount.coupon === true && discount.targetType === 'SHIPPING_LINE'
    );
    const isShippingFree = subscription.deliveryPriceAmount === 0 || shippingCoupon;

    // 送料無料チェック
    if (isShippingFree) {
      const message = settings['payBackPointsRate']['shippingFreeHtml'];
      setShippingFreeMessage(message);
    }

    // 小計
    const subtotal = subscription.subscriptionLines.reduce((total, line) => {
      return total + line.currentPriceAmount * line.quantity;
    }, 0);

    // 商品割引
    const discountCoupon = subscription.subscriptionDiscounts.filter(
      (discount) => discount.coupon === true && discount.targetType !== 'SHIPPING_LINE'
    );
    const discountCouponPrice = discountCoupon.reduce((total, discount) => {
      return total + Number(discount.actualAmount);
    }, 0);

    // 還元率チェック 還元率アップ不足金額設定
    const rate = calcPointsRateProgress(subtotal - discountCouponPrice);
    setPercent(rate.percent);
    setMissingAmount(rate.missingAmount);
    setProgressWidth(rate.progressWidth);

    const balloonHtml = settings['payBackPointsRate']['balloonHtml'] as BalloonHtml[];
    const matchValue = balloonHtml.find((item) => item.from <= subtotal && subtotal <= item.to);
    if (matchValue) {
      setBalloonHtml(matchValue);
    }
  }, [subscription, settings]);

  return (
    <div
      className={subscriptionListClass.rateContainer}
      style={{ opacity: subscription.status === 'ACTIVE' ? 'inherit' : '0.6' }}
    >
      {shippingFreeMessage !== '' ? (
        <RateShippingFreeContent
          percent={percent}
          shippingFreeMessage={shippingFreeMessage}
        />
      ) : (
        <RateOnlyContent percent={percent} />
      )}
      {balloonHtml && (
        <div className={subscriptionListClass.balloonContainer}>
          <div
            className={subscriptionListClass.balloonText}
            dangerouslySetInnerHTML={{
              __html: balloonHtml.balloonText.replace('{price}', missingAmount),
            }}
          />
          <div
            className={subscriptionListClass.balloonLink}
            onClick={() => {
              setPointsRateModal({
                title: balloonHtml.modal.title,
                text: balloonHtml.modal.text.replace('{price}', missingAmount),
                progressWidth: progressWidth,
              });
            }}
          >
            詳しく見る
          </div>
        </div>
      )}
    </div>
  );
};

const RateShippingFreeContent = ({ percent, shippingFreeMessage }) => {
  return (
    <div className={subscriptionListClass.rateShippingFreeContainer}>
      <div className={subscriptionListClass.rate}>
        ポイント還元率
        {percent === 5 ? (
          <p className={`${subscriptionListClass.percent} ${subscriptionListClass.maxPercent}`}>
            {percent}%<span>達成</span>
          </p>
        ) : (
          <p className={subscriptionListClass.percent}>{percent}%</p>
        )}
      </div>
      <div
        className={subscriptionListClass.rate}
        dangerouslySetInnerHTML={{
          __html: shippingFreeMessage,
        }}
      />
    </div>
  );
};

const RateOnlyContent = ({ percent }) => {
  return (
    <div className={subscriptionListClass.rateOnlyContainer}>
      ポイント還元率
      <span className={subscriptionListClass.percent}>{percent}%</span>
    </div>
  );
};
